import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import type { Project } from "@shared/schema";

interface ProjectContextValue {
  currentProject: Project | null;
  currentRunFolder: string | null;
  setCurrentProject: (project: Project | null) => void;
  setCurrentRunFolder: (folder: string | null) => void;
  clearProject: () => void;
}

const ProjectContext = createContext<ProjectContextValue>({
  currentProject: null,
  currentRunFolder: null,
  setCurrentProject: () => {},
  setCurrentRunFolder: () => {},
  clearProject: () => {},
});

const PROJECT_ID_KEY = "sparrow-current-project-id";
const RUN_FOLDER_KEY = "sparrow-run-folder";

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [currentRunFolder, setCurrentRunFolder] = useState<string | null>(
    () => localStorage.getItem(RUN_FOLDER_KEY),
  );

  // Auto-restore currentProject from localStorage on mount.
  // Handles page refreshes and any remaining hard-navigation edge cases
  // where the in-memory state was lost but the sidebar needs the project.
  useEffect(() => {
    if (currentProject) return; // already set
    const savedId = localStorage.getItem(PROJECT_ID_KEY);
    if (!savedId) return;
    fetch(`/api/projects/${savedId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((project) => {
        if (project) {
          setCurrentProject(project);
          // Don't overwrite runFolder — it's persisted independently
        } else {
          // Project was deleted; clean up stale reference
          localStorage.removeItem(PROJECT_ID_KEY);
        }
      })
      .catch(() => {
        // Offline or server not ready yet — leave null, user can re-select
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetProject = (project: Project | null) => {
    setCurrentProject(project);
    if (project) {
      localStorage.setItem(PROJECT_ID_KEY, project.id);
      if (project.runFolders?.[0]) {
        setCurrentRunFolder(project.runFolders[0]);
        localStorage.setItem(RUN_FOLDER_KEY, project.runFolders[0]);
      }
    } else {
      localStorage.removeItem(PROJECT_ID_KEY);
    }
  };

  const handleSetRunFolder = (folder: string | null) => {
    setCurrentRunFolder(folder);
    if (folder) localStorage.setItem(RUN_FOLDER_KEY, folder);
    else localStorage.removeItem(RUN_FOLDER_KEY);
  };

  const clearProject = () => {
    setCurrentProject(null);
    setCurrentRunFolder(null);
    localStorage.removeItem(PROJECT_ID_KEY);
    localStorage.removeItem(RUN_FOLDER_KEY);
  };

  return (
    <ProjectContext.Provider
      value={{
        currentProject,
        currentRunFolder,
        setCurrentProject: handleSetProject,
        setCurrentRunFolder: handleSetRunFolder,
        clearProject,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export const useProject = () => useContext(ProjectContext);
export const useProjectContext = useProject;
